// src/InputValidatorField.js
import React, {useContext} from "react";
import TextField from "@mui/material/TextField";
import { UseValidatorField } from "./Hooks/UseValidatorField";
import {InputAdornment, Tooltip} from "@mui/material";
import {ExclamationMark} from "@phosphor-icons/react";
import {ThemeContext} from "styled-components";

const InputValidatorField = ({
                                 handleChange,
                                 isValidInput = () => true,
                                 initialValue,
                                 value: controlledValue,
                                 InputLabelProps,
                                 InputProps,
                                 children,
                                 requirementsText,
                                 tooltipEnabled,
                                 error: externalError,
                                 sx,
                                 ...props
                             }) => {
    // Remove value from props to avoid conflicts - we'll set it explicitly
    const { value: propsValue, ...restProps } = props;
    // Support both controlled (value) and uncontrolled (initialValue) modes
    const isControlled = controlledValue !== undefined;
    
    // For uncontrolled mode, use the hook for state management
    // For controlled mode, we don't need the hook's state, but we still need error handling
    const uncontrolledHook = UseValidatorField(
        isValidInput, 
        isControlled ? undefined : initialValue
    );
    
    // Use controlled value if provided, otherwise use internal value from hook
    const value = isControlled ? (controlledValue || '') : uncontrolledHook.value;

    // For error handling:
    // - If external error is provided, use it (convert to boolean if string)
    // - For controlled mode, validate the controlled value directly
    // - For uncontrolled mode, use the hook's error function
    let error;
    if (externalError !== undefined) {
        // Convert externalError to boolean - if it's a string (error message), truthy = true
        const errorBool = typeof externalError === 'boolean' ? externalError : Boolean(externalError);
        error = () => errorBool;
    } else if (isControlled) {
        // For controlled mode, we need to track dirty state separately
        // For now, we'll validate on blur or if there's content
        const hasContent = value && value.length > 0;
        error = () => Boolean(hasContent && isValidInput && !isValidInput(value));
    } else {
        error = uncontrolledHook.error;
    }

    const handleInputChange = event => {
        // For controlled components, just call handleChange - parent manages state
        // For uncontrolled, update internal state first
        if (!isControlled) {
            uncontrolledHook.setValue(event.target.value);
        }
        handleChange(event);
    };

    const OnBlur = e => {
        if (!isControlled) {
            uncontrolledHook.onBlur();
        }
        handleChange(e);
    };

    const theme = useContext(ThemeContext)

    return (
        <>
            <style>
                {`
                    .white-border-input .MuiOutlinedInput-root fieldset {
                        border-color: ${theme.colors.secondary} !important;
                        border-width: 1px !important;
                        opacity: 1 !important;
                    }
                    .white-border-input .MuiOutlinedInput-root:hover fieldset {
                        border-color: ${theme.colors.text} !important;
                        border-width: 1px !important;
                        opacity: 1 !important;
                    }
                    .white-border-input .MuiOutlinedInput-root.Mui-focused fieldset {
                        border-color: ${theme.colors.text} !important;
                        border-width: 2px !important;
                        opacity: 1 !important;
                    }
                    .white-border-input .MuiOutlinedInput-root.Mui-error fieldset {
                        border-color: ${theme.colors.brandError} !important;
                        border-width: 2px !important;
                        opacity: 1 !important;
                    }
                    .white-border-input .MuiOutlinedInput-root legend {
                        color: ${theme.colors.secondary} !important;
                    }
                    .white-border-input .MuiOutlinedInput-root.Mui-error legend {
                        color: ${theme.colors.brandError} !important;
                    }
                    .white-border-input .MuiFormHelperText-root {
                        color: ${theme.colors.text} !important;
                        opacity: 1 !important;
                    }
                    .white-border-input .MuiFormHelperText-root.Mui-error {
                        color: ${theme.colors.brandError} !important;
                    }
                    .white-border-input.disabled .MuiFormHelperText-root {
                        color: ${theme.colors.textDisabled} !important;
                    }
                    /* When disabled, make the input text grey as well */
                    .white-border-input.disabled .MuiOutlinedInput-input {
                        color: ${theme.colors.textDisabled} !important;
                        -webkit-text-fill-color: ${theme.colors.textDisabled} !important;
                    }
                `}
            </style>
            <TextField
                fullWidth
                error={Boolean(error())}
                onBlur={OnBlur}
                onChange={handleInputChange}
                {...restProps}
                value={value || ''}
                margin="normal"
                variant="outlined"
                size="small"
                className={`white-border-input ${restProps.disabled ? 'disabled' : ''}`}
                InputLabelProps={{
                    ...InputLabelProps,
                    style: { color: theme.colors.text, ...InputLabelProps?.style },
                    shrink: true,
                }}
                InputProps={{
                    ...InputProps,
                    style: { color: theme.colors.text, ...InputProps?.style },
                    startAdornment: (tooltipEnabled && (value.length < 1 || error())) && (requirementsText && requirementsText.length > 0) && (
                        <InputAdornment position='start'>
                            <Tooltip title= {requirementsText} arrow>
                                <ExclamationMark size={32} color={theme.colors.secondary}/>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
                FormHelperTextProps={{
                    sx: {
                        color: theme.colors.text + ' !important', // Use theme white color with !important
                        fontSize:'.85rem'
                    },
                }}
                sx={{
                    flex: 1,
                    minWidth: 0, // Allow input to shrink
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1rem' }, // Minimum 16px to prevent iOS zoom
                        padding: { xs: '4px 8px', sm: '8px 14px' }, // Responsive padding
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: { xs: '1rem', sm: '1rem' }, // Minimum 16px to prevent iOS zoom
                    },
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '1rem' }, // Minimum 16px to prevent iOS zoom
                        padding: { xs: '8px 8px', sm: '10.5px 14px' }, // Responsive input padding
                    },
                    ...sx,
                }}
            />
            {children}
        </>
    );
};

export default InputValidatorField;
